/**
 * Mermaid `erDiagram` → DiagramData (tables + crow's-foot relationship edges).
 *
 * Subset:
 *   Header:      `erDiagram`
 *   Entities:    `CUSTOMER { string name PK\n string email }` blocks (bare names ok)
 *   Members:     `<type> <name> [PK|FK|UK] ["comment"]`
 *   Relations:   `A ||--o{ B : label`  (`--` identifying, `..` non-identifying)
 *                cardinality: || one, |o/o| zero-or-one, }|/|{ one-or-many, }o/o{ zero-or-many
 *
 * Relationships render as table-to-table crow's-foot edges (the `crowsfoot-*`
 * arrow markers), matching how Mermaid draws ER diagrams. Tables carry
 * `tableContent` so they reuse the structured database-table node + renderer.
 */

import type { DiagramData } from '../types/schema';

export interface ErdImportResult {
  data: DiagramData;
  warnings: string[];
}

/** Map a Mermaid cardinality token (||, o{, }|, …) to a crow's-foot arrow type. */
function cardToFoot(tok: string): string {
  const many = tok.includes('{') || tok.includes('}');
  const optional = tok.includes('o');
  if (many) return optional ? 'crowsfoot-zero-or-many' : 'crowsfoot-one-or-many';
  return optional ? 'crowsfoot-zero-or-one' : 'crowsfoot-one';
}

export function parseMermaidErd(text: string): ErdImportResult {
  const warnings: string[] = [];
  const tables = new Map<string, { name: string; columns: any[] }>();
  const ensure = (n: string) => {
    if (!tables.has(n)) tables.set(n, { name: n, columns: [] });
    return tables.get(n)!;
  };
  const edges: any[] = [];
  let cur: { name: string; columns: any[] } | null = null;

  const addMember = (t: { name: string; columns: any[] }, body: string) => {
    const m = body.trim().match(/^(\S+)\s+(\S+)(?:\s+(PK|FK|UK|PK,FK|FK,PK))?/i);
    if (!m) return;
    const keys = (m[3] || '').toUpperCase();
    t.columns.push({
      name: m[2],
      dataType: m[1],
      isPrimaryKey: keys.includes('PK'),
      isForeignKey: keys.includes('FK'),
      isUnique: keys.includes('UK'),
    });
  };

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('%%')) continue;
    if (/^erDiagram\b/.test(line)) continue;

    if (cur) {
      // a member line may carry a trailing `}` that closes the block
      const endsClose = line.endsWith('}');
      const body = endsClose ? line.slice(0, -1).trim() : line;
      if (body && body !== '}') addMember(cur, body);
      if (endsClose) cur = null;
      continue;
    }

    // entity block start — forgiving of trailing members on the brace line
    let m = line.match(/^(\w+)\s*\{(.*)$/);
    if (m) {
      cur = ensure(m[1]);
      let rest = m[2].trim();
      const closes = rest.endsWith('}');
      if (closes) rest = rest.slice(0, -1).trim();
      for (const part of rest.split(/\s*,\s*/)) if (part.trim()) addMember(cur, part.trim());
      if (closes) cur = null;
      continue;
    }

    // relationship
    m = line.match(/^(\w+)\s+([|}o]{1,2})(--|\.\.)([|{o]{1,2})\s+(\w+)\s*:\s*(.*)$/);
    if (m) {
      const [, a, lc, link, rc, b, label] = m;
      ensure(a); ensure(b);
      edges.push({
        id: `er-${a}-${b}-${edges.length}`,
        source: a,
        target: b,
        edgeType: 'one-to-many',
        type: 'custom',
        label: label.trim() || undefined,
        data: {
          arrowStart: cardToFoot(lc),
          arrowEnd: cardToFoot(rc),
          pathStyle: 'smoothstep',
          color: '#94a3b8',
          lineStyle: link === '..' ? 'dashed' : 'solid',
        },
      });
      continue;
    }

    warnings.push(`Unparsed line: ${line}`);
  }

  const nodes = [...tables.values()].map((t, i) => ({
    id: t.name,
    nodeType: 'table',
    label: t.name,
    position: { x: (i % 4) * 300, y: Math.floor(i / 4) * 240 },
    tableContent: { tableName: t.name, columns: t.columns },
  }));

  return { data: { version: '1.0', diagramType: 'erd', nodes, edges } as unknown as DiagramData, warnings };
}
